import { readConnectorResultCard } from './connector-result-card';
export function shopifyBatchApprovalMessage(batchId, selectedIds) {
  return `Approve ${selectedIds.length} changes.\nShopify batch: ${batchId}\nSelected items: ${JSON.stringify(selectedIds)}`;
}
export function shopifyBatchContinuationMessage(batchId, action) {
  return `${action === 'retry' ? 'Retry failed changes' : 'Finish remaining changes'}.\nShopify batch: ${batchId}`;
}
export function shopifyBatchDecisionMessage(batchId, action) {
  return `${action === 'not-now' ? 'Not now' : 'Review updated changes'}.\nShopify batch: ${batchId}`;
}
export function groupShopifyBatchFailures(items) {
  const counts = new Map();
  for (const item of items) {
    if (item.status === 'failed' && item.reason) {
      counts.set(item.reason, (counts.get(item.reason) ?? 0) + 1);
    }
  }
  return [...counts].map(_ref => {
    let reason = _ref[0],
      count = _ref[1];
    return {
      reason,
      count
    };
  }).sort((a, b) => b.count - a.count || (a.reason < b.reason ? -1 : a.reason > b.reason ? 1 : 0));
}
export function isShopifyBatchOwnerAction(text, batchId) {
  if ([shopifyBatchContinuationMessage(batchId, 'resume'), shopifyBatchContinuationMessage(batchId, 'retry'), shopifyBatchDecisionMessage(batchId, 'not-now'), shopifyBatchDecisionMessage(batchId, 'review-updated')].includes(text)) {
    return true;
  }
  const match = /^Approve ([1-9][0-9]*) changes\.\nShopify batch: ([^\n]+)\nSelected items: (\[[^\n]*\])$/.exec(text);
  if (!match || match[2] !== batchId) {
    return false;
  }
  let ids;
  try {
    ids = JSON.parse(match[3]);
  } catch {
    return false;
  }
  return Array.isArray(ids) && ids.length === Number(match[1]) && ids.every(id => typeof id === 'string') && new Set(ids).size === ids.length;
}
export function readShopifyBatchCard(value) {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).length > 128 * 1024) {
      return undefined;
    }
  } catch {
    return undefined;
  }
  const card = value;
  if (card.version !== 1 || typeof card.batchId !== 'string' || !card.batchId || typeof card.store !== 'string' || card.shopContext !== undefined && typeof card.shopContext !== 'string' || card.pushOnly !== undefined && (typeof card.pushOnly !== 'boolean' || card.pushOnly && card.state === 'review') || !['review', 'running', 'stopped', 'partial', 'done', 'stale'].includes(card.state ?? '') || !Array.isArray(card.items) || card.items.length === 0 || card.items.length > 500 || !Array.isArray(card.approvedIds) || !card.approvedIds.every(id => typeof id === 'string') || !Array.isArray(card.warnings) || !card.warnings.every(warning => typeof warning === 'string')) {
    return undefined;
  }
  const ids = new Set();
  for (const item of card.items) {
    if (!item || typeof item.id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(item.id) || ids.has(item.id) || typeof item.label !== 'string' || typeof item.before !== 'string' || typeof item.after !== 'string' || !['pending', 'in-flight', 'succeeded', 'failed', 'unknown', 'stale'].includes(item.status) || item.reason !== undefined && typeof item.reason !== 'string' || item.proposedImage !== undefined && !readShopifyBatchProposedImage(item.proposedImage)) {
      return undefined;
    }
    ids.add(item.id);
    if (item.product !== undefined && !readConnectorResultCard({
      connectorResultCard: {
        provider: 'shopify',
        items: [item.product]
      }
    })) {
      return undefined;
    }
  }
  if (!card.approvedIds.every(id => ids.has(id)) || new Set(card.approvedIds).size !== card.approvedIds.length) {
    return undefined;
  }
  return {
    ...card,
    items: card.items.map(item => {
      var _readConnectorResultC;
      return {
        ...item,
        product: item.product === undefined ? undefined : (_readConnectorResultC = readConnectorResultCard({
          connectorResultCard: {
            provider: 'shopify',
            items: [item.product]
          }
        })) == null ? void 0 : _readConnectorResultC.items[0],
        proposedImage: readShopifyBatchProposedImage(item.proposedImage)
      };
    })
  };
}
export function readShopifyBatchProposedImage(value) {
  if (!value || typeof value !== 'object' || !('fileName' in value) || !('url' in value) || !('mimeType' in value) || typeof value.fileName !== 'string' || typeof value.url !== 'string' || typeof value.mimeType !== 'string') {
    return undefined;
  }
  const match = /^[A-Za-z0-9_-]{1,180}(~mv2)?\.(png|jpe?g|webp|gif)$/.exec(value.fileName);
  if (!match || match[0] !== value.fileName) {
    return undefined;
  }
  const extension = match[2];
  if (value.mimeType !== `image/${extension === 'jpg' ? 'jpeg' : extension}` || !['media', 'ugd'].some(folder => value.url === `https://static.wixstatic.com/${folder}/${value.fileName}`)) {
    return undefined;
  }
  return {
    fileName: value.fileName,
    mimeType: value.mimeType,
    url: value.url
  };
}